import { RouteObject } from 'react-router-dom';
import { lazy } from 'react';

// Lazy loaded pages for better performance
const Home = lazy(() => import('../pages/home/page'));
const Services = lazy(() => import('../pages/services/page'));
const HealthSupport = lazy(() => import('../pages/health-support/page'));
const PharmacistResources = lazy(() => import('../pages/pharmacist-resources/page'));
const Information = lazy(() => import('../pages/information/page'));
const Career = lazy(() => import('../pages/career/page'));
const Entertainment = lazy(() => import('../pages/entertainment/page'));
const FamilyPharmacist = lazy(() => import('../pages/family-pharmacist/page'));
const SpecialtyPharmacy = lazy(() => import('../pages/specialty-pharmacy/page'));
const OTC = lazy(() => import('../pages/otc/page'));
const Kampo = lazy(() => import('../pages/kampo/page'));
const BeastBlog = lazy(() => import('../pages/beast-blog/page'));
const EffectiveMedication = lazy(() => import('../pages/beast-blog/effective-medication/page'));
const HealthManagement = lazy(() => import('../pages/beast-blog/health-management/page'));
const OTCSelection = lazy(() => import('../pages/beast-blog/otc-selection/page'));
const WinterMentalHealth = lazy(() => import('../pages/beast-blog/winter-mental-health/page'));
const MultidisciplinaryCollaboration = lazy(() => import('../pages/beast-blog/multidisciplinary-collaboration/page'));
const GenericMedicine = lazy(() => import('../pages/beast-blog/generic-medicine/page'));
const MedicineNotWorking = lazy(() => import('../pages/beast-blog/medicine-not-working/page'));
const SupplementDrugInteractions = lazy(() => import('../pages/beast-blog/supplement-drug-interactions/page'));
const ConstipationMedicineGuide = lazy(() => import('../pages/beast-blog/constipation-medicine-guide/page'));
const ElderlyMedicationManagement = lazy(() => import('../pages/beast-blog/elderly-medication-management/page'));
const SterilePreparation = lazy(() => import('../pages/sterile-preparation/page'));
const HomeCare = lazy(() => import('../pages/home-care/page'));
const DX = lazy(() => import('../pages/dx/page'));
const PackageInsertSearch = lazy(() => import('../pages/package-insert-search/page'));
const DrugPriceSearch = lazy(() => import('../pages/drug-price-search/page'));
const MedicalPurchase = lazy(() => import('../pages/medical-purchase/page'));
const PharmaceuticalWholesale = lazy(() => import('../pages/pharmaceutical-wholesale/page'));
const Emergency = lazy(() => import('../pages/emergency/page'));
const NotFound = lazy(() => import('../pages/NotFound'));
const OnlineMedicationGuidance = lazy(() => import('../pages/online-medication-guidance/page'));
const TheoryPage = lazy(() => import('../pages/theory/page'));
const TOCPage = lazy(() => import('../pages/theory/toc/page'));
const DBRPage = lazy(() => import('../pages/theory/dbr/page'));
const FlowEfficiencyPage = lazy(() => import('../pages/theory/flow-efficiency/page'));
const TaktThinkingPage = lazy(() => import('../pages/theory/takt-thinking/page'));
const SystemThinkingPage = lazy(() => import('../pages/theory/system-thinking/page'));
const SwissCheeseModelPage = lazy(() => import('../pages/theory/swiss-cheese-model/page'));
import HeinrichsLawPage from '../pages/theory/heinrichs-law/page';

// Motivation Pages
const MotivationPage = lazy(() => import('../pages/motivation/page'));
const MotivationTheoryPage = lazy(() => import('../pages/motivation/motivation-theory/page'));
const SelfDeterminationTheoryPage = lazy(() => import('../pages/motivation/self-determination-theory/page'));
const IntrinsicExtrinsicPage = lazy(() => import('../pages/motivation/intrinsic-extrinsic/page'));
const GoalSettingPage = lazy(() => import('../pages/motivation/goal-setting/page'));
const ReinforcementPage = lazy(() => import('../pages/motivation/reinforcement/page'));
const FeedbackPage = lazy(() => import('../pages/motivation/feedback/page'));
const NudgePage = lazy(() => import('../pages/motivation/nudge/page'));
const BehavioralEconomicsPage = lazy(() => import('../pages/motivation/behavioral-economics/page'));
const EquityPage = lazy(() => import('../pages/motivation/equity/page'));
const BurnoutPage = lazy(() => import('../pages/motivation/burnout/page'));
const EmotionalLaborPage = lazy(() => import('../pages/motivation/emotional-labor/page'));
const PsychologicalReactancePage = lazy(() => import('../pages/motivation/psychological-reactance/page'));

// Team Building Pages - Direct import
import TeamBuildingPage from '../pages/team-building/page';
import CoreConceptsPage from '../pages/team-building/core-concepts/page';
import FourElementsPage from '../pages/team-building/four-elements/page';
import PsychologicalSafetyPage from '../pages/team-building/psychological-safety/page';
import LeaderRolePage from '../pages/team-building/leader-role/page';
import SuccessFactorsPage from '../pages/team-building/success-factors/page';

// Sarcopenia Prevention Program Pages
const MuscleStrengthAssessment = lazy(() => import('../pages/sarcopenia/muscle-strength-assessment/page'));
const StrengthTrainingGuidance = lazy(() => import('../pages/sarcopenia/strength-training-guidance/page'));
const ProteinIntakeGuidance = lazy(() => import('../pages/sarcopenia/protein-intake-guidance/page'));
const ProgressMonitoring = lazy(() => import('../pages/sarcopenia/progress-monitoring/page'));

// Mental Health Pages
const StressAssessment = lazy(() => import('../pages/mental-health/stress-assessment/page'));
const RelaxationGuidance = lazy(() => import('../pages/mental-health/relaxation-guidance/page'));
const SleepImprovementSupport = lazy(() => import('../pages/mental-health/sleep-improvement-support/page'));
const SpecialistCollaboration = lazy(() => import('../pages/mental-health/specialist-collaboration/page'));

import HealthCheckupExplanation from '../pages/health-consultation/health-checkup-explanation/page';
import LifestyleDiseasePrevention from '../pages/health-consultation/lifestyle-disease-prevention/page';
import MedicationTherapyConsultation from '../pages/health-consultation/medication-therapy-consultation/page';
import HealthManagementPlan from '../pages/health-consultation/health-management-plan/page';
import DrugFoodInteractions from '../pages/beast-blog/drug-food-interactions/page';

// Beast Blog Pages
import BeastBlogPage from '../pages/beast-blog/page';
import AutumnHayFeverGuidePage from '../pages/beast-blog/autumn-hay-fever-guide/page';
import ConstipationMedicineGuidePage from '../pages/beast-blog/constipation-medicine-guide/page';
import DrugFoodInteractionsPage from '../pages/beast-blog/drug-food-interactions/page';
import EffectiveMedicationPage from '../pages/beast-blog/effective-medication/page';
import ElderlyMedicationManagementPage from '../pages/beast-blog/elderly-medication-management/page';
import FamilyPharmacistGuidePage from '../pages/beast-blog/family-pharmacist-guide/page';
import GenericMedicinePage from '../pages/beast-blog/generic-medicine/page';
import HealthManagementPage from '../pages/beast-blog/health-management/page';
import HomeMedicalCarePharmacistPage from '../pages/beast-blog/home-medical-care-pharmacist/page';
import InfluenzaPreventionPharmacyPage from '../pages/beast-blog/influenza-prevention-pharmacy/page';
import MedicineNotWorkingPage from '../pages/beast-blog/medicine-not-working/page';
import MultidisciplinaryCollaborationPage from '../pages/beast-blog/multidisciplinary-collaboration/page';
import OtcSelectionPage from '../pages/beast-blog/otc-selection/page';
import SupplementDrugInteractionsPage from '../pages/beast-blog/supplement-drug-interactions/page';
import WinterMentalHealthPage from '../pages/beast-blog/winter-mental-health/page';
import MedicalPlan5Diseases6ProjectsPage from '../pages/beast-blog/medical-plan-5diseases-6projects/page';
import PatientPharmacyVisionPage from '../pages/beast-blog/patient-pharmacy-vision/page';

import ContactPage from '../pages/contact/page';
import PrivacyPolicyPage from '../pages/privacy-policy/page';

import DisasterMedicationManagementPage from '../pages/beast-blog/disaster-medication-management/page';
import PharmacistSafetyCheckPage from '../pages/beast-blog/pharmacist-safety-check/page';

const LittlesLawPage = lazy(() => import("../pages/theory/littles-law/page"));

const DonabedianModelPage = lazy(() => import("../pages/theory/donabedian-model/page"));

const LabValuesPage = lazy(() => import("../pages/lab-values/page"));
const MangaPage = lazy(() => import("../pages/manga/page"));

const routes: RouteObject[] = [
  {
    path: '/',
    element: <Home />
  },
  {
    path: '/theory',
    element: <TheoryPage />
  },
  {
    path: '/theory/toc',
    element: <TOCPage />
  },
  {
    path: '/theory/dbr',
    element: <DBRPage />
  },
  {
    path: '/theory/flow-efficiency',
    element: <FlowEfficiencyPage />
  },
  {
    path: '/theory/takt-thinking',
    element: <TaktThinkingPage />
  },
  {
    path: '/theory/system-thinking',
    element: <SystemThinkingPage />
  },
  {
    path: '/theory/swiss-cheese-model',
    element: <SwissCheeseModelPage />
  },
  {
    path: '/theory/littles-law',
    element: <LittlesLawPage />
  },
  {
    path: '/theory/heinrichs-law',
    element: <HeinrichsLawPage />
  },
  {
    path: '/theory/donabedian-model',
    element: <DonabedianModelPage />
  },
  {
    path: '/theory/peak-end-rule',
    element: <TheoryPage />
  },
  {
    path: '/theory/abc-pareto',
    element: <TheoryPage />
  },
  {
    path: '/theory/nudge-theory',
    element: <TheoryPage />
  },
  {
    path: '/theory/yerkes-dodson',
    element: <TheoryPage />
  },
  {
    path: '/motivation',
    element: <MotivationPage />,
  },
  {
    path: '/motivation/motivation-theory',
    element: <MotivationTheoryPage />,
  },
  {
    path: '/motivation/self-determination-theory',
    element: <SelfDeterminationTheoryPage />,
  },
  {
    path: '/motivation/intrinsic-extrinsic',
    element: <IntrinsicExtrinsicPage />,
  },
  {
    path: '/motivation/goal-setting',
    element: <GoalSettingPage />,
  },
  {
    path: '/motivation/reinforcement',
    element: <ReinforcementPage />,
  },
  {
    path: '/motivation/feedback',
    element: <FeedbackPage />,
  },
  {
    path: '/motivation/nudge',
    element: <NudgePage />,
  },
  {
    path: '/motivation/behavioral-economics',
    element: <BehavioralEconomicsPage />,
  },
  {
    path: '/motivation/equity',
    element: <EquityPage />,
  },
  {
    path: '/motivation/burnout',
    element: <BurnoutPage />,
  },
  {
    path: '/motivation/emotional-labor',
    element: <EmotionalLaborPage />,
  },
  {
    path: '/motivation/psychological-reactance',
    element: <PsychologicalReactancePage />,
  },
  {
    path: '/team-building',
    element: <TeamBuildingPage />,
  },
  {
    path: '/team-building/core-concepts',
    element: <CoreConceptsPage />,
  },
  {
    path: '/team-building/four-elements',
    element: <FourElementsPage />,
  },
  {
    path: '/team-building/psychological-safety',
    element: <PsychologicalSafetyPage />,
  },
  {
    path: '/team-building/leader-role',
    element: <LeaderRolePage />,
  },
  {
    path: '/team-building/success-factors',
    element: <SuccessFactorsPage />,
  },
  {
    path: '/services',
    element: <Services />
  },
  {
    path: '/health-support',
    element: <HealthSupport />
  },
  {
    path: '/pharmacist-resources',
    element: <PharmacistResources />
  },
  {
    path: '/information',
    element: <Information />
  },
  {
    path: '/career',
    element: <Career />
  },
  {
    path: '/entertainment',
    element: <Entertainment />
  },
  {
    path: '/family-pharmacist',
    element: <FamilyPharmacist />
  },
  {
    path: '/specialty-pharmacy',
    element: <SpecialtyPharmacy />
  },
  {
    path: '/otc',
    element: <OTC />
  },
  {
    path: '/kampo',
    element: <Kampo />
  },
  {
    path: '/online-medication-guidance',
    element: <OnlineMedicationGuidance />
  },
  {
    path: '/beast-blog',
    element: <BeastBlogPage />
  },
  {
    path: '/beast-blog/autumn-hay-fever-guide',
    element: <AutumnHayFeverGuidePage />
  },
  {
    path: '/beast-blog/constipation-medicine-guide',
    element: <ConstipationMedicineGuidePage />
  },
  {
    path: '/beast-blog/drug-food-interactions',
    element: <DrugFoodInteractionsPage />
  },
  {
    path: '/beast-blog/effective-medication',
    element: <EffectiveMedicationPage />
  },
  {
    path: '/beast-blog/elderly-medication-management',
    element: <ElderlyMedicationManagementPage />
  },
  {
    path: '/beast-blog/family-pharmacist-guide',
    element: <FamilyPharmacistGuidePage />
  },
  {
    path: '/beast-blog/generic-medicine',
    element: <GenericMedicinePage />
  },
  {
    path: '/beast-blog/health-management',
    element: <HealthManagementPage />
  },
  {
    path: '/beast-blog/home-medical-care-pharmacist',
    element: <HomeMedicalCarePharmacistPage />
  },
  {
    path: '/beast-blog/influenza-prevention-pharmacy',
    element: <InfluenzaPreventionPharmacyPage />
  },
  {
    path: '/beast-blog/medicine-not-working',
    element: <MedicineNotWorkingPage />
  },
  {
    path: '/beast-blog/multidisciplinary-collaboration',
    element: <MultidisciplinaryCollaborationPage />
  },
  {
    path: '/beast-blog/otc-selection',
    element: <OtcSelectionPage />
  },
  {
    path: '/beast-blog/supplement-drug-interactions',
    element: <SupplementDrugInteractionsPage />
  },
  {
    path: '/beast-blog/winter-mental-health',
    element: <WinterMentalHealthPage />
  },
  {
    path: '/beast-blog/medical-plan-5diseases-6projects',
    element: <MedicalPlan5Diseases6ProjectsPage />
  },
  {
    path: '/beast-blog/patient-pharmacy-vision',
    element: <PatientPharmacyVisionPage />
  },
  {
    path: '/beast-blog/disaster-medication-management',
    element: <DisasterMedicationManagementPage />
  },
  {
    path: '/beast-blog/pharmacist-safety-check',
    element: <PharmacistSafetyCheckPage />
  },
  {
    path: '/sterile-preparation',
    element: <SterilePreparation />
  },
  {
    path: '/home-care',
    element: <HomeCare />
  },
  {
    path: '/dx',
    element: <DX />
  },
  {
    path: '/package-insert-search',
    element: <PackageInsertSearch />
  },
  {
    path: '/drug-price-search',
    element: <DrugPriceSearch />
  },
  {
    path: '/medical-purchase',
    element: <MedicalPurchase />
  },
  {
    path: '/pharmaceutical-wholesale',
    element: <PharmaceuticalWholesale />
  },
  {
    path: '/emergency',
    element: <Emergency />
  },
  // Aliases pointing to existing pages
  {
    path: '/health-consultation',
    element: <HealthSupport />
  },
  {
    path: '/self-care',
    element: <HealthSupport />
  },
  {
    path: '/official-links',
    element: <Information />
  },
  {
    path: '/guidelines',
    element: <Information />
  },
  {
    path: '/news',
    element: <Information />
  },
  {
    path: '/online-guidance',
    element: <PharmacistResources />
  },

  // Health Consultation Routes
  {
    path: '/health-consultation/health-checkup-explanation',
    element: <HealthCheckupExplanation />
  },
  {
    path: '/health-consultation/lifestyle-disease-prevention',
    element: <LifestyleDiseasePrevention />
  },
  {
    path: '/health-consultation/medication-therapy-consultation',
    element: <MedicationTherapyConsultation />
  },
  {
    path: '/health-consultation/health-management-plan',
    element: <HealthManagementPlan />
  },

  // Sarcopenia Prevention Program Routes
  {
    path: '/sarcopenia/muscle-strength-assessment',
    element: <MuscleStrengthAssessment />
  },
  {
    path: '/sarcopenia/strength-training-guidance',
    element: <StrengthTrainingGuidance />
  },
  {
    path: '/sarcopenia/protein-intake-guidance',
    element: <ProteinIntakeGuidance />
  },
  {
    path: '/sarcopenia/progress-monitoring',
    element: <ProgressMonitoring />
  },

  // Mental Health Routes
  {
    path: '/mental-health/stress-assessment',
    element: <StressAssessment />
  },
  {
    path: '/mental-health/relaxation-guidance',
    element: <RelaxationGuidance />
  },
  {
    path: '/mental-health/sleep-improvement-support',
    element: <SleepImprovementSupport />
  },
  {
    path: '/mental-health/specialist-collaboration',
    element: <SpecialistCollaboration />
  },

  {
    path: '/contact',
    element: <ContactPage />,
  },
  {
    path: '/privacy-policy',
    element: <PrivacyPolicyPage />,
  },

  {
    path: '/lab-values',
    element: <LabValuesPage />,
  },
  {
    path: '/manga',
    element: <MangaPage />,
  },

  {
    path: '*',
    element: <NotFound />
  }
];

export default routes;